import React, { useState, useEffect } from "react";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { Delete, Person, WhatsApp, CalendarToday, Add, Email, Check, Close, Folder } from '@mui/icons-material';
import { FaqButton } from "../components/FaqButton";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { TextField, MenuItem, IconButton, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Chip, Dialog, DialogTitle, DialogContent, Tooltip, Box, Pagination, FormControl, Select, CircularProgress, Checkbox, ListItemText, Menu, ListItemIcon } from '@mui/material';
import { Bolt, AssignmentInd } from '@mui/icons-material';
import ContractStandingChip from '../components/ContractStandingChip';
import QuickPatientRegisterModal from "../components/modals/QuickPatientRegisterModal";
import ConfirmModal from "../components/modals/ConfirmModal";

import { colors, typography } from '../theme/designSystem';
import ClearFiltersButton from '../components/ClearFiltersButton';
import AddButton from '../components/AddButton';
import ResultCount from '../components/ResultCount';
import StandardPagination from '../components/Pagination/StandardPagination';
import { titleCaseName } from '../utils/nameCase';
import { useSensitiveDataAccess } from '../hooks/useSensitiveDataAccess';
import { SensitiveCell } from '../components/SensitiveField';
import {
  patientsService,
  PatientListItem,
  PatientListOptions,
  QuickRegisterResult,
} from "../services/patientsService";
import { formatPhoneDisplay, toE164Digits, whatsAppLink } from '../utils/phone';

const SEARCH_MAX = 100;

interface PatientsListProps {
  /** Rota "patient-quick-register" (menu + Cadastro rápido): abre a lista com o modal já aberto */
  initialQuickRegisterOpen?: boolean;
}

/**
 * Avatar do paciente (item 50): com foto no storage, busca a MINIATURA (lista) ou a foto cheia
 * (modal) como blob autenticado; sem foto no storage, cai para a URL herdada do Bubble; sem nada, ícone.
 */
const PatientAvatar: React.FC<{ patient: PatientListItem; size: number; full?: boolean; onClick?: (e: React.MouseEvent) => void }> =
  ({ patient, size, full = false, onClick }) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [visible, setVisible] = useState(full);
    const ref = React.useRef<HTMLDivElement | null>(null);

    // Miniatura só quando a linha entra na tela: 50 linhas por página não viram 50 requisições de uma vez
    useEffect(() => {
      if (visible || !ref.current || typeof IntersectionObserver === 'undefined') { if (!visible) setVisible(true); return; }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { setVisible(true); observer.disconnect(); }
      }, { rootMargin: '200px' });
      observer.observe(ref.current);
      return () => observer.disconnect();
    }, [visible]);

    useEffect(() => {
      if (!patient.hasPhoto || !visible) { setBlobUrl(null); return; }
      let alive = true;
      let url: string | null = null;
      patientsService.getPhoto(patient.id, !full)
        .then((u) => { url = u; if (alive) setBlobUrl(u); else URL.revokeObjectURL(u); })
        .catch(() => { if (alive) setBlobUrl(null); });
      return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }, [patient.id, patient.hasPhoto, full, visible]);

    const legacy = patient.patientPicture
      ? (patient.patientPicture.startsWith('//') ? `https:${patient.patientPicture}` : patient.patientPicture)
      : undefined;

    return (
      <Avatar
        ref={ref}
        src={blobUrl ?? legacy}
        alt={patient.patientName}
        onClick={onClick}
        sx={{
          width: size,
          height: size,
          margin: full ? '0 auto 1rem' : undefined,
          backgroundColor: '#03B4C6',
          cursor: onClick ? 'pointer' : 'default',
          fontSize: full ? '4rem' : undefined,
        }}
      >
        <Person sx={full ? { fontSize: '6rem' } : undefined} />
      </Avatar>
    );
  };

const PatientsList: React.FC<PatientsListProps> = ({ initialQuickRegisterOpen = false }) => {
  const { user } = useAuth();
  // Criar paciente e ABRIR o prontuario sao permissoes diferentes (RN-112p): a tela de cadastro
  // completo e a mesma que abre o paciente existente, entao "tem a tela" nao responde "pode criar".
  const podeCriarPaciente = (user?.allowedPages || []).includes("patient-create");
  // Excluir tambem e permissao da ACAO (RN-112s): `patients` e a da lista, e o Profissional a tem.
  const podeExcluirPaciente = (user?.allowedPages || []).includes("patient-delete");
  // Indicativo e filtro de contrato (RN-558e) só para quem vê a aba Contratos do paciente
  const podeVerContratos = (user?.allowedPages || []).includes("patient-contracts");
  const { goToDashboard, goToPatientRegister } = useNavigation();
  const { toast, showToast, hideToast } = useToast();
  // RN-112q: a API já mascara; as flags só sinalizam a célula e escondem os links de contato
  const { restricted: sensitiveRestricted } = useSensitiveDataAccess();

  // Estados dos filtros (server-side)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Todos' | 'Ativo' | 'Inativo' | 'Teste'>('Ativo');
  const [genderFilter, setGenderFilter] = useState('Todos');
  const [professionalsFilter, setProfessionalsFilter] = useState<string[]>([]);
  const [completeFilter, setCompleteFilter] = useState<'Todos' | 'Completo' | 'Incompleto'>('Todos');
  const [contractFilter, setContractFilter] = useState<'Todos' | 'Vigente' | 'Pendente'>('Todos');

  // Opções dos combos (profissionais e gêneros da entidade da sessão)
  const [listOptions, setListOptions] = useState<PatientListOptions>({ professionals: [], genders: [], documentTypes: [], countries: [] });

  // Dados da lista
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // Estados da paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Estado da ordenação — 'created' = default do backend (data de cadastro decrescente)
  const [sortField, setSortField] = useState<'created' | 'name' | 'document' | 'age'>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Estados dos modais (exclusão e foto)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<PatientListItem | null>(null);
  const [photoPatient, setPhotoPatient] = useState<PatientListItem | null>(null);

  // Menu do botão de adicionar (Cadastro rápido / Cadastro completo)
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [isQuickRegisterOpen, setIsQuickRegisterOpen] = useState(
    initialQuickRegisterOpen && (user?.allowedPages || []).includes("patient-create"));
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [hasFilterChanges, setHasFilterChanges] = useState(false);

  // Valores iniciais dos filtros
  const initialFilters = {
    searchTerm: '',
    statusFilter: 'Ativo' as 'Todos' | 'Ativo' | 'Inativo' | 'Teste',
    genderFilter: 'Todos',
    completeFilter: 'Todos' as 'Todos' | 'Completo' | 'Incompleto',
    contractFilter: 'Todos' as 'Todos' | 'Vigente' | 'Pendente',
    sortOrder: 'asc' as 'asc' | 'desc'
  };

  // Debounce da busca (a lista consulta o servidor)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Combos carregados uma vez
  useEffect(() => {
    patientsService.getListOptions()
      .then((opts) => {
        setListOptions(opts);
        // Mesma regra da Agenda (RN-523b): perfil 'own' já vem filtrado e travado no próprio profissional
        if (opts.dataScope === 'own' && (opts.ownProfessionalIds ?? []).length > 0) {
          setProfessionalsFilter(opts.ownProfessionalIds ?? []);
        }
      })
      .catch(() => showToast('Erro ao carregar os filtros de profissionais e sexo biológico.', 'error'));
  }, [showToast]);

  // Carga da lista — filtros, ordenação e paginação resolvidos no backend
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // "Teste" lista só os pacientes de teste (RN-552); nas demais opções eles ficam escondidos
    const status = statusFilter === 'Todos' || statusFilter === 'Teste' ? 'all' : statusFilter === 'Inativo' ? 'inactive' : 'active';
    const test = statusFilter === 'Teste' ? 'only' : 'hide';
    // 'created' = sem sortBy (default do backend: cadastro decrescente).
    // Idade cresce quando a data de nascimento diminui — ordenar por idade inverte o sentido
    const sortBy = sortField === 'created' ? undefined : sortField === 'age' ? 'birthDate' : sortField;
    const apiSortOrder = sortField === 'created' ? undefined : sortField === 'age' ? (sortOrder === 'asc' ? 'desc' : 'asc') : sortOrder;

    patientsService.getAll({
      pageNumber: currentPage,
      pageSize: itemsPerPage,
      test,
      search: debouncedSearch || undefined,
      status,
      genderId: genderFilter !== 'Todos' ? genderFilter : undefined,
      professionals: professionalsFilter.length > 0 ? professionalsFilter.join(',') : undefined,
      completeRegister: completeFilter === 'Todos' ? undefined : completeFilter === 'Completo',
      contract: contractFilter === 'Vigente' ? 'vigente' : contractFilter === 'Pendente' ? 'pendente' : undefined,
      sortBy,
      sortOrder: apiSortOrder,
    })
      .then((result) => {
        if (cancelled) return;
        setPatients(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(() => {
        if (cancelled) return;
        showToast('Erro ao carregar a lista de pacientes.', 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [debouncedSearch, statusFilter, genderFilter, professionalsFilter, completeFilter, contractFilter, sortField, sortOrder, currentPage, itemsPerPage, reloadKey, showToast]);

  // Função para calcular idade
  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  };

  // Detectar mudanças nos filtros
  useEffect(() => {
    const hasChanges =
      searchTerm !== initialFilters.searchTerm ||
      statusFilter !== initialFilters.statusFilter ||
      genderFilter !== initialFilters.genderFilter ||
      professionalsFilter.length > 0 ||
      completeFilter !== initialFilters.completeFilter ||
      contractFilter !== initialFilters.contractFilter ||
      sortField !== 'created' ||
      sortOrder !== initialFilters.sortOrder;

    setHasFilterChanges(hasChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, genderFilter, professionalsFilter, completeFilter, sortOrder]);

  // Calcular paginação (dados do servidor)
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Função para rolar para o início da lista
  const scrollToTop = () => {
    const listContainer = document.querySelector('.patients-list-container');
    if (listContainer) {
      const containerRect = listContainer.getBoundingClientRect();
      const offset = 100; // Margem superior para não ficar escondido pelo header
      const targetPosition = window.pageYOffset + containerRect.top - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    } else {
      // Fallback: rolar para o topo da página
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setTimeout(scrollToTop, 100);
  };

  const handleSort = (field: 'name' | 'document' | 'age') => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Reset página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, genderFilter, professionalsFilter, completeFilter]);

  // Função para formatar data de nascimento
  const formatBirthDate = (birthDate: string): string => {
    const date = new Date(birthDate);
    return date.toLocaleDateString('pt-BR');
  };

  // Conectivos ficam minúsculos no Title Case de nomes PT-BR
  const patientPhone = (patient: PatientListItem): string =>
    formatPhoneDisplay(patient.patientPhoneDdi, patient.patientPhoneCode, patient.patientPhone) ?? '';

  const responsiblePhone = (patient: PatientListItem): string =>
    formatPhoneDisplay(patient.responsiblePhoneDdi1, patient.responsiblePhoneCode1, patient.responsiblePhone1) ?? '';

  // Coluna Celular: o do paciente; sem ele, o do responsável 1
  const displayPhone = (patient: PatientListItem): string =>
    patientPhone(patient) || responsiblePhone(patient);

  // Contato (email/WhatsApp) é com o responsável 1; sem ele, direto com o paciente
  const contactEmail = (patient: PatientListItem): string =>
    patient.responsibleEmail1 || patient.patientEmail || '';

  const contactPhone = (patient: PatientListItem): string =>
    responsiblePhone(patient) || patientPhone(patient);

  const clearFilters = () => {
    setSearchTerm(initialFilters.searchTerm);
    setStatusFilter(initialFilters.statusFilter);
    setGenderFilter(initialFilters.genderFilter);
    setProfessionalsFilter([]);
    setCompleteFilter(initialFilters.completeFilter);
    setContractFilter(initialFilters.contractFilter);
    setSortField('created');
    setSortOrder(initialFilters.sortOrder);
    setCurrentPage(1);
    setHasFilterChanges(false);
  };

  const handleQuickRegisterSuccess = (result: QuickRegisterResult) => {
    if (result.emailSent) {
      showToast('Paciente cadastrado. Email de preenchimento enviado.', 'success');
    } else if (result.intakeLink) {
      showToast('Paciente cadastrado. O email não foi enviado neste ambiente — o link de preenchimento está disponível.', 'warning');
    } else {
      showToast('Paciente cadastrado. Complete o cadastro quando possível.', 'success');
    }
    setReloadKey(prev => prev + 1);
  };

  const handlePatientRowClick = (patientId: string) => {
    goToPatientRegister(patientId);
  };

  const handleDeleteConfirm = async () => {
    if (!patientToDelete || deleting) return;

    setDeleting(true);
    try {
      await patientsService.remove(patientToDelete.id, deleteReason.trim());
      showToast(`Paciente ${titleCaseName(patientToDelete.patientName)} excluído.`, 'success');
      setIsDeleteModalOpen(false);
      setPatientToDelete(null);
      setDeleteReason('');
      setReloadKey(prev => prev + 1);
    } catch (error: any) {
      const detail = error?.response?.data?.description;
      showToast(detail || 'Erro ao excluir o paciente. Tente novamente.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (deleting) return;
    setIsDeleteModalOpen(false);
    setPatientToDelete(null);
    setDeleteReason('');
  };

  const handlePatientAction = (action: string, patientId: string) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;

    if (action === 'email') {
      const email = contactEmail(patient);
      if (!email) {
        showToast('Paciente sem email de contato cadastrado.', 'warning');
        return;
      }
      const subject = encodeURIComponent(`Contato - ${titleCaseName(patient.patientName)}`);
      window.location.href = `mailto:${email}?subject=${subject}`;
    } else if (action === 'whatsapp') {
      const contact = patient.responsiblePhone1
        ? toE164Digits(patient.responsiblePhoneDdi1, patient.responsiblePhoneCode1, patient.responsiblePhone1)
        : toE164Digits(patient.patientPhoneDdi, patient.patientPhoneCode, patient.patientPhone);
      if (!contact) {
        showToast('Paciente sem celular de contato cadastrado.', 'warning');
        return;
      }
      const teamName = user?.entityName || user?.clinicName || 'Clinic4Us';
      const link = whatsAppLink(contact, `Olá, esperamos que estejam bem! Aqui é da equipe ${teamName}`);
      if (link) window.open(link, '_blank');
    } else if (action === 'calendar') {
      // Agenda em nova aba com o paciente selecionado (a página de agenda lê patientId ao ser integrada)
      const scheduleUrl = `${window.location.origin}${window.location.pathname}?page=schedule&patientId=${patient.id}&patientName=${encodeURIComponent(titleCaseName(patient.patientName))}`;
      window.open(scheduleUrl, '_blank');
    } else if (action === 'cadastro') {
      goToPatientRegister(patient.id);
    } else if (action === 'delete') {
      setPatientToDelete(patient);
      setIsDeleteModalOpen(true);
    }
  };

  const handleLogoClick = () => {
    goToDashboard();
  };

  return (
    <div className="patients-list">
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={handleLogoClick}
      />

      <main className="c4u-main">
        <div className="c4u-page">
            {/* Título da Lista de Pacientes */}
            <div className="c4u-page-header c4u-page-header--in-panel">
              <div className="c4u-page-header__texts">
                <h1 className="c4u-page-header__title">Lista de pacientes</h1>
                <p className="c4u-page-header__subtitle">
                  Visualize, pesquise e gerencie todos os pacientes cadastrados no sistema.
                </p>
              </div>
              <div className="c4u-page-header__actions">
                <FaqButton />
              </div>
            </div>

          {/* Container principal: Filtros + Paginação + Tabela */}
          <Paper elevation={0} className="c4u-panel">
            {/* Filtros da lista de pacientes */}
            <Box sx={{ borderBottom: 'none' }}>
            <div className="c4u-filters__row">
            <div className="c4u-filters__fields">
              {/* Busca por nome, documento ou responsável */}
              <TextField
                label="Nome do paciente"
                placeholder="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: SEARCH_MAX }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    '& fieldset': {
                      borderColor: '#ced4da',
                      legend: {
                        maxWidth: '100%',
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: '#ced4da',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#03B4C6',
                      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.95rem',
                    color: '#6c757d',
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    '&.Mui-focused': {
                      color: '#03B4C6',
                    },
                  },
                }}
              />

              {/* Status */}
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#ced4da',
                      legend: {
                        maxWidth: '100%',
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: '#ced4da',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#03B4C6',
                      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                    },
                  },
                  '& .MuiSelect-select': {
                    padding: '0.375rem 0.5rem',
                    color: '#495057',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.95rem',
                    color: '#6c757d',
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    '&.Mui-focused': {
                      color: '#03B4C6',
                    },
                  },
                }}
              >
                <MenuItem value="Ativo">Ativo</MenuItem>
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="Inativo">Inativo</MenuItem>
                <MenuItem value="Teste">Teste</MenuItem>
              </TextField>

              {/* Sexo biológico */}
              <TextField
                select
                label="Sexo biológico"
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#ced4da',
                      legend: {
                        maxWidth: '100%',
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: '#ced4da',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#03B4C6',
                      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                    },
                  },
                  '& .MuiSelect-select': {
                    padding: '0.375rem 0.5rem',
                    color: '#495057',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.95rem',
                    color: '#6c757d',
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    '&.Mui-focused': {
                      color: '#03B4C6',
                    },
                  },
                }}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                {listOptions.genders.map((gender) => (
                  <MenuItem key={gender.id} value={gender.id}>
                    {gender.name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Filtro por profissional (múltipla seleção); travado para o perfil 'own' */}
              <TextField
                select
                label="Profissionais"
                disabled={listOptions.dataScope === 'own'}
                value={professionalsFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  const ids = typeof value === 'string' ? value.split(',') : value;
                  // "Todos" limpa a seleção (seleção vazia = sem filtro)
                  setProfessionalsFilter(ids.includes('__all__') ? [] : ids);
                }}
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const ids = selected as string[];
                    if (ids.length === 0) return 'Todos';
                    return listOptions.professionals
                      .filter((p) => ids.includes(p.id))
                      .map((p) => p.name)
                      .join(', ');
                  },
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#ced4da',
                      legend: {
                        maxWidth: '100%',
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: '#ced4da',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#03B4C6',
                      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                    },
                  },
                  '& .MuiSelect-select': {
                    padding: '0.375rem 0.5rem',
                    color: '#495057',
                    fontSize: '1rem',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.95rem',
                    color: '#6c757d',
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    '&.Mui-focused': {
                      color: '#03B4C6',
                    },
                  },
                }}
              >
                <MenuItem value="__all__">
                  <Checkbox
                    size="small"
                    checked={professionalsFilter.length === 0}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <ListItemText primary="Todos" />
                </MenuItem>
                {listOptions.professionals.map((professional) => (
                  <MenuItem key={professional.id} value={professional.id}>
                    <Checkbox
                      size="small"
                      checked={professionalsFilter.includes(professional.id)}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <ListItemText primary={professional.name} />
                  </MenuItem>
                ))}
              </TextField>

              {/* Filtro por cadastro completo */}
              <TextField
                select
                label="Cadastro"
                value={completeFilter}
                onChange={(e) => setCompleteFilter(e.target.value as any)}
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#ced4da',
                      legend: {
                        maxWidth: '100%',
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: '#ced4da',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#03B4C6',
                      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                    },
                  },
                  '& .MuiSelect-select': {
                    padding: '0.375rem 0.5rem',
                    color: '#495057',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.95rem',
                    color: '#6c757d',
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    '&.Mui-focused': {
                      color: '#03B4C6',
                    },
                  },
                }}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="Completo">Completo</MenuItem>
                <MenuItem value="Incompleto">Incompleto</MenuItem>
              </TextField>

              {/* Situação do contrato (RN-558e): só para quem vê a aba Contratos */}
              {podeVerContratos && (
                <TextField
                  select
                  label="Contrato"
                  value={contractFilter}
                  onChange={(e) => setContractFilter(e.target.value as 'Todos' | 'Vigente' | 'Pendente')}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      '& fieldset': { borderColor: '#ced4da', legend: { maxWidth: '100%' } },
                      '&:hover fieldset': { borderColor: '#ced4da' },
                      '&.Mui-focused fieldset': { borderColor: '#03B4C6', boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)' },
                    },
                    '& .MuiSelect-select': { padding: '0.375rem 0.5rem', color: '#495057' },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.95rem',
                      color: '#6c757d',
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      '&.Mui-focused': { color: '#03B4C6' },
                    },
                  }}
                >
                  <MenuItem value="Todos">Todos</MenuItem>
                  <MenuItem value="Vigente">Vigente</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                </TextField>
              )}
            </div>
              {/* Ações da barra: limpar e incluir, no fim, como manda o design system.
                  Incluir exige `patient-create` (RN-112p): a lista é aberta por quem só consulta —
                  o Profissional, por exemplo —, e o botão não tinha verificação nenhuma. */}
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                {podeCriarPaciente && (
                <AddButton
                  onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                  title="Adicionar novo paciente"
                />
                )}
                <Menu
                  anchorEl={addMenuAnchor}
                  open={addMenuAnchor !== null}
                  onClose={() => setAddMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => { setAddMenuAnchor(null); setIsQuickRegisterOpen(true); }}
                  >
                    <ListItemIcon><Bolt fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Cadastro rápido" secondary="Dados mínimos, em um modal" />
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setAddMenuAnchor(null); goToPatientRegister(); }}
                  >
                    <ListItemIcon><AssignmentInd fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Cadastro completo" secondary="Página com todos os dados" />
                  </MenuItem>
                </Menu>
              </div>
            </div>
          </Box>

          {/* Paginação + Tabela */}
            <ResultCount shown={totalCount} />

            {/* Lista de pacientes */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#03B4C6' }} />
              </Box>
            ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              className="patients-list-container"
              sx={{
                boxShadow: 'none',
                border: 0,
                overflowX: 'auto'
              }}
            >
            <Table
              sx={{
                minWidth: 1280,
                // Larguras fixas por coluna: com layout automático o navegador dava a folga à coluna
                // "Completo" (1 palavra) e espremia o nome do paciente (achado do Hilton, 04/09/2026)
                tableLayout: 'fixed',
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#e9ecef',
                    height: 48,
                    '& .MuiTableCell-root': { paddingTop: 0, paddingBottom: 0 },
                  }}
                >
                  <TableCell sx={{ width: '60px', fontWeight: 600, fontSize: '0.875rem', color: '#495057', padding: '12px 16px' }}>
                    Foto
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 'auto',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#495057',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                      }
                    }}
                    onClick={() => handleSort('name')}
                    title="Ordenar por nome"
                  >
                    Nome do paciente {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '130px',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#495057',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                      }
                    }}
                    onClick={() => handleSort('document')}
                    title="Ordenar por documento"
                  >
                    Documento {sortField === 'document' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                  </TableCell>
                  <TableCell sx={{ width: '120px', fontWeight: 600, fontSize: '0.875rem', color: '#495057', padding: '12px 16px' }}>
                    Nascimento
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '80px',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#495057',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                      }
                    }}
                    onClick={() => handleSort('age')}
                    title="Ordenar por idade"
                  >
                    Idade {sortField === 'age' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                  </TableCell>
                  <TableCell sx={{ width: '220px', fontWeight: 600, fontSize: '0.875rem', color: '#495057', padding: '12px 16px' }}>
                    Responsável
                  </TableCell>
                  <TableCell sx={{ width: '130px', fontWeight: 600, fontSize: '0.875rem', color: '#495057', padding: '12px 16px' }}>
                    Celular
                  </TableCell>
                  <TableCell sx={{ width: '100px', fontWeight: 600, fontSize: '0.875rem', color: '#495057', padding: '12px 16px', textAlign: 'center' }}>
                    Completo
                  </TableCell>
                  <TableCell sx={{ width: '240px', fontWeight: 600, fontSize: '0.875rem', color: '#495057', padding: '12px 16px', textAlign: 'right' }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', color: '#6c757d', padding: '24px 16px' }}>
                      Nenhum paciente encontrado com os filtros atuais.
                    </TableCell>
                  </TableRow>
                )}
                {patients.map((patient, index) => (
                  <TableRow
                    key={patient.id}
                    onClick={() => handlePatientRowClick(patient.id)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafbfc',
                      '&:hover': {
                        backgroundColor: '#f0f9fa',
                      }
                    }}
                  >
                    <TableCell data-label="Foto" sx={{ padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      <PatientAvatar
                        patient={patient}
                        size={40}
                        onClick={(e) => { e.stopPropagation(); setPhotoPatient(patient); }}
                      />
                    </TableCell>
                    <TableCell data-label="Nome do paciente" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#495057', padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      {titleCaseName(patient.patientName)}
                      {!patient.isActive && (
                        <Chip
                          label="Inativo"
                          size="small"
                          sx={{ ml: 1, backgroundColor: '#6c757d', color: 'white', height: '20px', fontSize: '0.7rem' }}
                        />
                      )}
                      {patient.isTest && (
                        <Chip
                          label="Teste"
                          size="small"
                          sx={{ ml: 1, backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffe08a', height: '20px', fontSize: '0.7rem', fontWeight: 700 }}
                        />
                      )}
                      <ContractStandingChip standing={patient.contract} />
                    </TableCell>
                    <TableCell data-label="Documento" sx={{ fontSize: '0.875rem', color: '#495057', padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      {sensitiveRestricted('documents') ? <SensitiveCell /> : patient.patientDocument ? patient.patientDocument.replace(/[.\-\s/]/g, '') : '—'}
                    </TableCell>
                    <TableCell data-label="Nascimento" sx={{ fontSize: '0.875rem', color: '#495057', padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      {patient.patientBirthDate ? formatBirthDate(patient.patientBirthDate) : '—'}
                    </TableCell>
                    <TableCell data-label="Idade" sx={{ fontSize: '0.875rem', color: '#495057', padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      {patient.patientBirthDate ? calculateAge(patient.patientBirthDate) : '—'}
                    </TableCell>
                    <TableCell data-label="Responsável" sx={{ fontSize: '0.875rem', color: '#495057', padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      {titleCaseName(patient.responsibleName1) || '—'}
                    </TableCell>
                    <TableCell data-label="Telefone" sx={{ fontSize: '0.875rem', color: '#495057', padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      {sensitiveRestricted('phones') ? <SensitiveCell /> : displayPhone(patient) || '—'}
                    </TableCell>
                    <TableCell data-label="Completo" sx={{ padding: '12px 16px', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {patient.completeRegister
                          ? <Check sx={{ fontSize: '1rem', color: colors.success }} />
                          : <Close sx={{ fontSize: '1rem', color: colors.error }} />}
                        <Typography sx={{ fontSize: '0.875rem', color: colors.textPrimary }}>
                          {patient.completeRegister ? 'Sim' : 'Não'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell data-label="Ações" sx={{ padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Tooltip title={sensitiveRestricted('emails') ? 'Restrito' : (contactEmail(patient) || 'Sem email de contato')} arrow>
                          <span>
                          <IconButton
                            size="small"
                            disabled={sensitiveRestricted('emails')}
                            onClick={(e) => { e.stopPropagation(); handlePatientAction('email', patient.id); }}
                            sx={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#f57c00',
                              },
                              '&.Mui-disabled': { backgroundColor: '#e9ecef', color: '#adb5bd' },
                            }}
                          >
                            <Email sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={sensitiveRestricted('phones') ? 'Restrito' : (contactPhone(patient) || 'Sem celular de contato')} arrow>
                          <span>
                          <IconButton
                            size="small"
                            disabled={sensitiveRestricted('phones')}
                            onClick={(e) => { e.stopPropagation(); handlePatientAction('whatsapp', patient.id); }}
                            sx={{
                              backgroundColor: '#25D366',
                              color: 'white',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#1da851',
                              },
                              '&.Mui-disabled': { backgroundColor: '#e9ecef', color: '#adb5bd' },
                            }}
                          >
                            <WhatsApp sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Agendar consulta" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePatientAction('calendar', patient.id); }}
                            sx={{
                              backgroundColor: '#2196f3',
                              color: 'white',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#1976d2',
                              }
                            }}
                          >
                            <CalendarToday sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Gerenciar cadastro" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePatientAction('cadastro', patient.id); }}
                            sx={{
                              backgroundColor: '#03a9f4',
                              color: 'white',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#0288d1',
                              }
                            }}
                          >
                            <Folder sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        {podeExcluirPaciente && (
                        <Tooltip title="Excluir paciente" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePatientAction('delete', patient.id); }}
                            sx={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#c82333',
                              }
                            }}
                          >
                            <Delete sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
            )}

            <StandardPagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalCount}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            />
          </Paper>
        </div>
      </main>

      <FooterInternal
        className="login-footer-component"
      />

      {/* Modal de confirmação de exclusão (padrão do sistema) */}
      <ConfirmModal
        isOpen={isDeleteModalOpen && patientToDelete !== null}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o paciente ${titleCaseName(patientToDelete?.patientName || null)}?`}
        warningMessage="O paciente sai das listas e da operação. A reativação só pode ser feita pela administração do sistema."
        confirmButtonText="Excluir paciente"
        reasonLabel="Motivo da exclusão"
        reasonValue={deleteReason}
        onReasonChange={setDeleteReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
        isProcessing={deleting}
      />

      {/* Modal da foto do paciente (clique no thumb) — padrão de modal do sistema */}
      <Dialog
        open={photoPatient !== null}
        onClose={() => setPhotoPatient(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            maxWidth: '420px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontSize: '1.4rem', fontWeight: 600, margin: 0 }}
          >
            {photoPatient ? titleCaseName(photoPatient.patientName) : ''}
          </Typography>
          <IconButton
            onClick={() => setPhotoPatient(null)}
            sx={{
              color: colors.white,
              padding: '0.25rem',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important', textAlign: 'center' }}>
          {photoPatient && <PatientAvatar patient={photoPatient} size={220} full />}
          {photoPatient?.patientBirthDate && (
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              {calculateAge(photoPatient.patientBirthDate)} anos — nascimento em {formatBirthDate(photoPatient.patientBirthDate)}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Cadastro rápido (componente reutilizável) */}
      <QuickPatientRegisterModal
        open={isQuickRegisterOpen}
        onClose={() => setIsQuickRegisterOpen(false)}
        onSuccess={handleQuickRegisterSuccess}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default PatientsList;
